import React, { useEffect } from "react";
import { UUID } from "src/api/api_common";
import { GetSingleSubnetResponse } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

/** Data provided by the {@link SINGLE_SUBNET_CONTEXT} */
export type SingleSubnetContext = {
    /** The currently available group */
    subnet: GetSingleSubnetResponse;

    /** Reload the groups' information */
    reset: () => void;
};

/** {@link React.Context} to access the groups */
const SINGLE_SUBNET_CONTEXT = React.createContext<SingleSubnetContext>({
    subnet: {
        uuid: "",
        access_rules: { outbound: [], inbound: [] },
        has_router: true,
        name: "",
        description: "",
        rulesets: { outbound: [], inbound: [] },
        possible_routers: [],
        routers: [],
    },

    /**
     * Reset the group
     */
    reset: () => {},
});
SINGLE_SUBNET_CONTEXT.displayName = "SingleSubnet";
export default SINGLE_SUBNET_CONTEXT;

/**
 * The properties for {@link SingleSubnetProvider}
 */
export type SingleSubnetProviderProps = {
    /**
     * The specific subnet
     */
    uuid: UUID;

    /**
     * Children of the provider
     */
    children: React.ReactNode;
};

/**
 * The provider of a tags context
 */
export function SingleSubnetProvider(props: SingleSubnetProviderProps) {
    const [subnet, setSubnet] = React.useState<GetSingleSubnetResponse | "loading">("loading");
    let fetching = false;

    /**
     * Fetch the subnet
     */
    const fetchSubnet = async () => {
        if (fetching) return;
        fetching = true;

        const res = await ApiInternal.subnets.getSingle(props.uuid);
        setSubnet(res);

        fetching = false;
    };

    useEffect(() => {
        fetchSubnet().then();
    }, [props.uuid]);

    if (subnet === "loading") {
        return <div>Loading ..</div>;
    }

    return (
        <SINGLE_SUBNET_CONTEXT.Provider
            value={{
                subnet: subnet,
                reset: fetchSubnet,
            }}
        >
            {props.children}
        </SINGLE_SUBNET_CONTEXT.Provider>
    );
}
